import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Download,
  Upload,
  FileSpreadsheet,
  Sparkles,
  Printer,
  Loader2,
  CreditCard,
} from "lucide-react";
import {
  DataTable,
  MasterCrudPage,
  PageTitle,
  useRouteModulePermissions,
} from "@/components/common/Crud";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { downloadCSV, parseCSV } from "@/lib/csv";
import { BACKEND_URL } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ModernLoader from "@/components/common/ModernLoader";

const SAMPLE_CSV_HEADERS = [
  "first_name",
  "last_name",
  "gender",
  "date_of_birth",
  "mobile",
  "email",
  "class_id",
  "section_id",
  "father_first_name",
  "father_mobile",
  "mother_first_name",
  "address",
  "city",
  "state",
  "pin",
  "aadhaar",
  "apaar_id",
  "pen",
];

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function extractImageValue(source) {
  if (!source) return "";
  if (Array.isArray(source)) {
    for (const item of source) {
      const found = extractImageValue(item);
      if (found) return found;
    }
    return "";
  }
  if (typeof source === "object") {
    const directKeys = [
      "photo_url",
      "photo",
      "image",
      "avatar",
      "file_id",
      "id",
    ];
    for (const key of directKeys) {
      const value = source[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    const nestedKeys = [
      "student_photo",
      "doc_student_photo",
      "documents",
      "payload_json",
    ];
    for (const key of nestedKeys) {
      const value = extractImageValue(source[key]);
      if (value) return value;
    }
    return "";
  }
  if (typeof source === "string") {
    const raw = source.trim();
    if (!raw) return "";
    if (
      (raw.startsWith("{") && raw.endsWith("}")) ||
      (raw.startsWith("[") && raw.endsWith("]"))
    ) {
      try {
        return extractImageValue(JSON.parse(raw));
      } catch {
        return raw;
      }
    }
    return raw;
  }
  return "";
}

function resolveImageUrl(source) {
  const raw = String(source || "").trim();
  if (!raw) return "";
  if (
    raw.startsWith("data:") ||
    raw.startsWith("blob:") ||
    /^https?:\/\//i.test(raw)
  ) {
    return raw;
  }
  if (
    raw.startsWith("/api/") ||
    raw.startsWith("/uploads/") ||
    raw.startsWith("/files/")
  ) {
    return BACKEND_URL ? `${BACKEND_URL}${raw}` : raw;
  }
  if (/^[a-zA-Z0-9_-]+$/.test(raw) && !raw.includes(".")) {
    return BACKEND_URL
      ? `${BACKEND_URL}/api/files/${raw}`
      : `/api/files/${raw}`;
  }
  if (raw.startsWith("uploads/")) {
    return BACKEND_URL ? `${BACKEND_URL}/${raw}` : `/${raw}`;
  }
  return BACKEND_URL ? `${BACKEND_URL}/uploads/${raw}` : `/uploads/${raw}`;
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "—";
  if (typeof value === "object")
    return value.name || value.label || JSON.stringify(value);
  return String(value);
}

function isSubmittedDocument(value) {
  if (value == null || value === "") return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return !!extractImageValue(value);
  return String(value).trim() !== "";
}

function humanizeKey(key) {
  return String(key)
    .replace(/^doc_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function buildStudentPrintHtml(student, className, schoolInfo = {}) {
  const schoolName = String(
    schoolInfo.school_name || schoolInfo.name || "VREENDA SCHOOL",
  ).trim();
  const schoolAddress = String(
    schoolInfo.address ||
      schoolInfo.school_address ||
      [schoolInfo.city, schoolInfo.state, schoolInfo.pin]
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .join(", "),
  ).trim();
  const schoolContact = [
    schoolInfo.phone,
    schoolInfo.mobile,
    schoolInfo.contact_no,
    schoolInfo.phone_no,
    schoolInfo.phone_number,
    schoolInfo.telephone,
    schoolInfo.email,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" | ");
  const schoolLogoSource =
    schoolInfo.logo_url || schoolInfo.logo || schoolInfo.school_logo || "";
  const schoolLogoUrl = resolveImageUrl(
    extractImageValue(schoolLogoSource) || schoolLogoSource,
  );
  const photoSource =
    student?.photo_url ||
    extractImageValue(student?.documents) ||
    extractImageValue(student?.payload_json) ||
    extractImageValue(student?.doc_student_photo) ||
    extractImageValue(student?.student_photo) ||
    student?.photo ||
    student?.image ||
    student?.avatar ||
    "";
  const photoUrl = resolveImageUrl(photoSource);
  const fullName =
    `${student?.first_name || ""} ${student?.middle_name || ""} ${student?.last_name || ""}`
      .replace(/\s+/g, " ")
      .trim();

  const sections = [
    {
      title: "Student Details",
      fields: [
        ["Admission No", student?.admission_no],
        ["Admission Date", student?.admission_date],
        ["Admission Year", student?.admission_year],
        ["Student Type", student?.student_type],
        ["Name", fullName],
        ["Class", className],
        ["Section", student?.section],
        ["Roll No", student?.roll_no],
        ["Has Sibling", student?.has_sibling],
        [
          "Sibling Student",
          student?.sibling_student_name || student?.sibling_student_id,
        ],
        ["Gender", student?.gender],
        ["DOB", student?.date_of_birth],
        ["Place of Birth", student?.place_of_birth],
        ["Nationality", student?.nationality],
        ["Category", student?.category],
        ["Caste", student?.caste],
        ["Religion", student?.religion],
        ["Mobile", student?.mobile],
        ["Email", student?.email],
        ["Blood Group", student?.blood_group],
        ["Height", student?.height],
        ["Weight", student?.weight],
        ["House", student?.house_id],
        ["Club", student?.club_id],
        ["RTE", student?.rte],
      ],
    },
    {
      title: "National IDs",
      fields: [
        ["Aadhaar", student?.aadhaar],
        ["APAAR ID", student?.apaar_id],
        ["PEN", student?.pen],
        ["UDISE+ PEN", student?.udise_pen],
        ["DigiLocker ID", student?.digilocker_id],
        ["SSSM ID", student?.sssm_id],
        ["Family ID", student?.family_id],
        ["Passport No", student?.passport_no],
        ["Passport Valid Till", student?.passport_valid_till],
        ["Board", student?.board],
        ["Board Roll No", student?.board_roll_no],
        ["SR No", student?.sr_no],
        ["PEN Issue Date", student?.pen_issue_date],
      ],
    },
    {
      title: "Father Details",
      fields: [
        ["Father First Name", student?.father_first_name],
        ["Father Middle Name", student?.father_middle_name],
        ["Father Last Name", student?.father_last_name],
        ["Father Mobile", student?.father_mobile],
        ["Father Qualification", student?.father_qualification],
        ["Father Occupation", student?.father_occupation],
        ["Father Income", student?.father_yearly_income],
        ["Father Email", student?.father_email],
        ["Father PAN", student?.father_pan],
        ["Father Aadhaar", student?.father_aadhaar],
      ],
    },
    {
      title: "Mother Details",
      fields: [
        ["Mother First Name", student?.mother_first_name],
        ["Mother Middle Name", student?.mother_middle_name],
        ["Mother Last Name", student?.mother_last_name],
        ["Mother Mobile", student?.mother_mobile],
        ["Mother Qualification", student?.mother_qualification],
        ["Mother Occupation", student?.mother_occupation],
        ["Mother Income", student?.mother_yearly_income],
        ["Mother Email", student?.mother_email],
        ["Mother PAN", student?.mother_pan],
        ["Mother Aadhaar", student?.mother_aadhaar],
      ],
    },
    {
      title: "Guardian & Emergency",
      fields: [
        ["Guardian Type", student?.guardian_type],
        ["Guardian First Name", student?.guardian_first_name],
        ["Guardian Last Name", student?.guardian_last_name],
        ["Guardian Mobile", student?.guardian_mobile],
        ["Guardian Qualification", student?.guardian_qualification],
        ["Guardian Occupation", student?.guardian_occupation],
        ["Guardian Email", student?.guardian_email],
        ["Guardian PAN", student?.guardian_pan],
        ["Guardian Aadhaar", student?.guardian_aadhaar],
        ["Guardian Relation", student?.guardian_relation],
        ["Guardian Address", student?.guardian_address],
        ["Emergency Contact Type", student?.emergency_contact_type],
        ["Emergency Contact Name", student?.emergency_name],
        ["Emergency Mobile", student?.emergency_mobile],
        ["Emergency Phone", student?.emergency_phone],
        ["Emergency Address", student?.emergency_address],
      ],
    },
    {
      title: "Address",
      fields: [
        ["Current Address 1", student?.addr_line1],
        ["Current Address 2", student?.addr_line2],
        ["City", student?.city],
        ["State", student?.state],
        ["District", student?.district],
        ["Country", student?.country],
        ["Pin", student?.pin],
        ["Same as Current", student?.same_as_current],
        ["Permanent Address 1", student?.perm_addr_line1],
        ["Permanent Address 2", student?.perm_addr_line2],
        ["Permanent City", student?.perm_city],
        ["Permanent State", student?.perm_state],
        ["Permanent District", student?.perm_district],
        ["Permanent Country", student?.perm_country],
        ["Permanent Pin", student?.perm_pin],
      ],
    },
    {
      title: "Medical & Services",
      fields: [
        ["Allergies", student?.allergies],
        ["Chronic Disease", student?.chronic_disease],
        ["Disability Type", student?.disability_type],
        ["Special Needs", student?.special_needs],
        ["Doctor Name", student?.doctor_name],
        ["Doctor Contact", student?.doctor_contact],
        ["Asthma", student?.asthma],
        ["Heart Disease", student?.heart_disease],
        ["AC Transport", student?.ac_transport],
        ["AC Hostel", student?.ac_hostel],
        ["Extracurricular", student?.extracurricular],
        ["Career Counseling", student?.career_counseling],
        ["Stationery", student?.stationery],
        ["Books", student?.books],
        ["Mess", student?.mess],
        ["RTE Service", student?.rte_service],
        ["Competitive Exam", student?.competitive_exam],
        ["Shadow Teacher", student?.shadow_teacher],
      ],
    },
  ];

  const seenKeys = new Set([
    "admission_no",
    "admission_date",
    "admission_year",
    "student_type",
    "first_name",
    "middle_name",
    "last_name",
    "class_id",
    "section",
    "roll_no",
    "has_sibling",
    "sibling_student_name",
    "sibling_student_id",
    "gender",
    "date_of_birth",
    "place_of_birth",
    "nationality",
    "category",
    "caste",
    "religion",
    "mobile",
    "email",
    "blood_group",
    "height",
    "weight",
    "house_id",
    "club_id",
    "rte",
    "aadhaar",
    "apaar_id",
    "pen",
    "udise_pen",
    "digilocker_id",
    "sssm_id",
    "family_id",
    "passport_no",
    "passport_valid_till",
    "board",
    "board_roll_no",
    "sr_no",
    "pen_issue_date",
    "father_first_name",
    "father_middle_name",
    "father_last_name",
    "father_mobile",
    "father_qualification",
    "father_occupation",
    "father_yearly_income",
    "father_email",
    "father_pan",
    "father_aadhaar",
    "mother_first_name",
    "mother_middle_name",
    "mother_last_name",
    "mother_mobile",
    "mother_qualification",
    "mother_occupation",
    "mother_yearly_income",
    "mother_email",
    "mother_pan",
    "mother_aadhaar",
    "guardian_type",
    "guardian_first_name",
    "guardian_last_name",
    "guardian_mobile",
    "guardian_qualification",
    "guardian_occupation",
    "guardian_email",
    "guardian_pan",
    "guardian_aadhaar",
    "guardian_relation",
    "guardian_address",
    "emergency_contact_type",
    "emergency_name",
    "emergency_mobile",
    "emergency_phone",
    "emergency_address",
    "addr_line1",
    "addr_line2",
    "city",
    "state",
    "district",
    "country",
    "pin",
    "same_as_current",
    "perm_addr_line1",
    "perm_addr_line2",
    "perm_city",
    "perm_state",
    "perm_district",
    "perm_country",
    "perm_pin",
    "allergies",
    "chronic_disease",
    "disability_type",
    "special_needs",
    "doctor_name",
    "doctor_contact",
    "asthma",
    "heart_disease",
    "ac_transport",
    "ac_hostel",
    "extracurricular",
    "career_counseling",
    "stationery",
    "books",
    "mess",
    "rte_service",
    "competitive_exam",
    "shadow_teacher",
    "photo_url",
    "photo",
    "image",
    "avatar",
    "file_id",
    "student_photo",
    "doc_student_photo",
    "documents",
    "payload_json",
    // Internal/system bookkeeping — duplicates of fields already shown
    // above under their proper labels, or plain DB housekeeping that has
    // no place on a printed profile.
    "id",
    "branch_id",
    "session_id",
    "section_id",
    "status",
    "sections",
    "dob",
    "father_name",
    "mother_name",
    "is_rte",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ]);

  // Catch-all for any other raw id/audit columns not explicitly named above
  // (e.g. new fields added later) — a printed profile should never show a
  // database row id or a created/updated-by/at stamp.
  const isInternalKey = (key) =>
    /(^id$|_id$|^is_|_at$|_by$)/i.test(key) || key === "status";

  const extraFields = Object.entries(student || {})
    .filter(
      ([key, value]) =>
        !seenKeys.has(key) &&
        !isInternalKey(key) &&
        value != null &&
        value !== "",
    )
    .filter(
      ([key, value]) => typeof value !== "object" || Array.isArray(value),
    );

  const extraSection = extraFields.length
    ? {
        title: "Additional Details",
        fields: extraFields.map(([key, value]) => [humanizeKey(key), value]),
      }
    : null;

  const printSections = extraSection ? [...sections, extraSection] : sections;
  const page1Titles = new Set(["Student Details", "National IDs"]);
  const page1Sections = printSections.filter((section) =>
    page1Titles.has(section.title),
  );
  const page2Sections = printSections.filter(
    (section) => !page1Titles.has(section.title),
  );

  return `<!doctype html><html><head><title>Student Profile</title><style>
    *{box-sizing:border-box}
    body{font-family:Arial,sans-serif;margin:0;padding:18px;color:#0f172a;background:#fff}
    .sheet{max-width:1100px;margin:0 auto}
    .top{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:18px;border:1px solid #cbd5e1;border-radius:14px;background:linear-gradient(135deg,#f8fafc 0%,#eef2ff 100%);margin-bottom:14px}
    .brand{display:flex;align-items:center;gap:12px;margin-bottom:10px}
    .brand-logo{width:58px;height:58px;border-radius:14px;border:1px solid #cbd5e1;overflow:hidden;background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0}
    .brand-logo img{width:100%;height:100%;object-fit:contain;display:block}
    .brand-fallback{width:100%;height:100%;display:grid;place-items:center;font-weight:800;font-size:20px;color:#4338ca;background:#fff}
    .title{font-size:22px;font-weight:800;margin:0 0 6px 0}
    .sub{font-size:12px;color:#475569;margin:0 0 4px 0}
    .meta{font-size:11px;color:#64748b}
    .photo{width:130px;height:160px;border-radius:12px;border:1px solid #cbd5e1;overflow:hidden;background:#fff;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:11px;font-weight:700;text-align:center}
    .photo img{width:100%;height:100%;object-fit:cover;display:block}
    .section{border:1px solid #cbd5e1;border-radius:12px;margin-bottom:12px;overflow:hidden;break-inside:avoid}
    .section h3{margin:0;padding:10px 14px;font-size:13px;background:#f8fafc;border-bottom:1px solid #e2e8f0}
    .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0}
    .row{display:grid;grid-template-columns:180px 1fr;border-bottom:1px solid #eef2f7}
    .row:nth-last-child(-n+2){border-bottom:none}
    .label{padding:8px 12px;font-size:11px;font-weight:700;color:#334155;border-right:1px solid #eef2f7;background:#fbfdff}
    .value{padding:8px 12px;font-size:11px;word-break:break-word}
    .row.full{grid-column:1 / -1}
    .row.full .label{border-right:none}
    .docs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;padding:12px}
    .doc{border:1px dashed #cbd5e1;border-radius:10px;padding:10px;font-size:11px;color:#475569;display:flex;align-items:flex-start;gap:8px;min-height:58px}
    .doc-icon{width:18px;height:18px;border-radius:5px;border:1px solid #94a3b8;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;font-size:11px;line-height:1}
    .doc-icon.checked{background:#16a34a;border-color:#16a34a;color:#fff}
    .doc-icon.unchecked{background:#fff;color:#94a3b8}
    .doc-text{display:flex;flex-direction:column;gap:2px}
    .doc-label{font-weight:700;color:#1e293b}
    .doc-status{font-size:10px;text-transform:uppercase;letter-spacing:.04em}
    .doc-status.submitted{color:#15803d}
    .doc-status.not-submitted{color:#b91c1c}
    @media print{body{padding:0}.sheet{max-width:none}.section{break-inside:avoid}.page-break{break-before:page}}
  </style><script>window.onload=function(){setTimeout(function(){window.print();},250);};</script></head><body>
    <div class="sheet">
      <div class="top">
        <div>
          <div class="brand">
            <div class="brand-logo">${schoolLogoUrl ? `<img src="${escapeHtml(schoolLogoUrl)}" alt="School Logo" />` : `<span class="brand-fallback">S</span>`}</div>
            <div>
              <h1 class="title">${escapeHtml(schoolName || "School ERP")}</h1>
              ${schoolAddress ? `<div class="meta">${escapeHtml(schoolAddress)}</div>` : ""}
              ${schoolContact ? `<div class="meta">${escapeHtml(schoolContact)}</div>` : ""}
            </div>
          </div>
          <p class="sub" style="margin-top:10px;font-size:14px;font-weight:700;color:#0f172a;">${escapeHtml(fullName || "Unnamed Student")}</p>
          <div class="meta">Admission No: ${escapeHtml(student?.admission_no || "—")} | Class: ${escapeHtml(className || "—")} | Section: ${escapeHtml(student?.section || "—")} | Status: ${escapeHtml(student?.status || "—")}</div>
        </div>
        <div class="photo">${photoUrl ? `<img src="${escapeHtml(photoUrl)}" alt="Student Photo" />` : "STUDENT PHOTO"}</div>
      </div>
      ${page1Sections
        .map(
          (section) => `
          <div class="section">
            <h3>${escapeHtml(section.title)}</h3>
            <div class="grid">
              ${section.fields
                .map(
                  ([label, value]) =>
                    `<div class="row"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(displayValue(value))}</div></div>`,
                )
                .join("")}
            </div>
          </div>`,
        )
        .join("")}
      <div class="page-break"></div>
      ${page2Sections
        .map(
          (section) => `
          <div class="section">
            <h3>${escapeHtml(section.title)}</h3>
            <div class="grid">
              ${section.fields
                .map(
                  ([label, value]) =>
                    `<div class="row"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(displayValue(value))}</div></div>`,
                )
                .join("")}
            </div>
          </div>`,
        )
        .join("")}
      <div class="section page-break">
        <h3>Documents</h3>
        <div class="docs">
          ${[
            ["Birth Certificate", student?.doc_birth_certificate],
            ["Aadhaar Card", student?.doc_aadhaar_card],
            ["Previous Marksheet", student?.doc_prev_marksheet],
            ["Transfer Certificate", student?.doc_transfer_certificate],
            ["Migration Certificate", student?.doc_migration_certificate],
            ["Hostel Affidavit", student?.doc_hostel_affidavit],
            ["Grade 10 Marksheet", student?.doc_grade10_marksheet],
            ["Bank Passbook", student?.doc_bank_passbook],
            ["Address Proof", student?.doc_address_proof],
            ["Vaccination Certificate", student?.doc_vaccination_certificate],
            ["Student Photo", student?.doc_student_photo],
            ["Father Photo", student?.doc_father_photo],
            ["Mother Photo", student?.doc_mother_photo],
          ]
            .map(([label, value]) => {
              const submitted = isSubmittedDocument(value);
              return `<div class="doc">
                  <span class="doc-icon ${submitted ? "checked" : "unchecked"}">${submitted ? "✓" : ""}</span>
                  <div class="doc-text">
                    <span class="doc-label">${escapeHtml(label)}</span>
                    <span class="doc-status ${submitted ? "submitted" : "not-submitted"}">${submitted ? "Submitted" : "Not Submitted"}</span>
                  </div>
                </div>`;
            })
            .join("")}
        </div>
      </div>
    </div>
  </body></html>`;
}

async function fetchAuthedBlob(path) {
  const normalizedPath = String(path || "").replace(/^\/api(?=\/)/i, "");
  const response = await api.get(normalizedPath, { responseType: "blob" });
  const disposition = response.headers?.["content-disposition"] || "";
  const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return { blob: response.data, fileName: fileNameMatch?.[1] || null };
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "download.pdf";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function downloadAuthedReport(path, fallbackFileName) {
  const { blob, fileName } = await fetchAuthedBlob(path);
  triggerBlobDownload(blob, fileName || fallbackFileName);
}

async function previewAuthedReport(path) {
  const { blob } = await fetchAuthedBlob(path);
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    URL.revokeObjectURL(url);
    throw new Error("Popup blocked. Allow popups to preview.");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function extractErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.detail || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.detail || fallback;
}

export default function Students() {
  const { branchId, sessionId } = useAuth();
  const modulePermissions = useRouteModulePermissions("students");
  const directoryPermissions = useRouteModulePermissions(
    "students",
    "directory",
  );
  const promotePermissions = useRouteModulePermissions("students", "promote");
  const ptmPermissions = useRouteModulePermissions("students", "ptms");
  const ptmAttendancePermissions = useRouteModulePermissions(
    "students",
    "ptm_attendance",
  );
  const nav = useNavigate();
  const fileRef = useRef();
  const [activeTab, setActiveTab] = useState("directory");
  const [rows, setRows] = useState([]);
  const [disabledRows, setDisabledRows] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [q, setQ] = useState("");
  const [classId, setClassId] = useState("");
  const [section, setSection] = useState("");
  const [deleteRow, setDeleteRow] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState({});
  const [idCardClassId, setIdCardClassId] = useState("");
  const [idCardSection, setIdCardSection] = useState("");
  const [idCardGenerating, setIdCardGenerating] = useState(false);
  const [importState, setImportState] = useState({
    isImporting: false,
    total: 0,
    processed: 0,
    ok: 0,
    fail: 0,
  });

  const [promoteRows, setPromoteRows] = useState([]);
  const [promoteClassId, setPromoteClassId] = useState("");
  const [promoteSection, setPromoteSection] = useState("");
  const [promoteQ, setPromoteQ] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [newSessionId, setNewSessionId] = useState("");
  const [newClassId, setNewClassId] = useState("");
  const [newSectionId, setNewSectionId] = useState("");
  const [carryForwardDues, setCarryForwardDues] = useState(true);

  const [ptms, setPtms] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  const visibleTabs = [
    directoryPermissions.canView && "directory",
    directoryPermissions.canView && "disabled",
    directoryPermissions.canView && "id-cards",
    promotePermissions.canView && "promote",
    ptmPermissions.canView && "ptms",
    ptmAttendancePermissions.canView && "ptm-attendance",
  ].filter(Boolean);

  const load = useCallback(async () => {
    setDirectoryLoading(true);
    const params = { q: q || undefined };
    if (branchId) params.branch_id = branchId;
    if (sessionId) params.session_id = sessionId;
    if (classId) params.class_id = classId;
    if (section) params.section = section;
    try {
      const { data } = await api.get("/students", { params });
      setRows(data.items || []);
    } finally {
      setDirectoryLoading(false);
    }
  }, [branchId, sessionId, q, classId, section]);

  const loadDisabled = useCallback(async () => {
    setDirectoryLoading(true);
    const params = { q: q || undefined, status: "disabled" };
    if (branchId) params.branch_id = branchId;
    if (sessionId) params.session_id = sessionId;
    if (classId) params.class_id = classId;
    if (section) params.section = section;
    try {
      const { data } = await api.get("/students", { params });
      setDisabledRows(data.items || []);
    } finally {
      setDirectoryLoading(false);
    }
  }, [branchId, sessionId, q, classId, section]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeTab === "disabled") {
      loadDisabled();
    }
  }, [activeTab, loadDisabled]);

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [activeTab, visibleTabs]);
  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([
        api.get("/classes"),
        api.get("/sessions"),
      ]);
      const classesList = c.data.items || [];
      const sessionsList = s.data.items || [];
      setClasses(classesList);
      setSessions(sessionsList);
      if (!newSessionId && sessionId) setNewSessionId(sessionId);
      if (!newSessionId && sessionsList[0]?.id)
        setNewSessionId(sessionsList[0].id);
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/school_info", {
          params: { branch_id: branchId || undefined },
        });
        setSchoolInfo(data?.value || data || {});
      } catch {
        setSchoolInfo({});
      }
    })();
  }, [branchId]);

  const loadPromoteRows = useCallback(async () => {
    const params = {
      limit: 1000,
      branch_id: branchId || undefined,
      session_id: sessionId || undefined,
      q: promoteQ || undefined,
    };
    if (promoteClassId) params.class_id = promoteClassId;
    if (promoteSection) params.section = promoteSection;
    const { data } = await api.get("/students", { params });
    setPromoteRows(data.items || []);
  }, [branchId, sessionId, promoteClassId, promoteSection, promoteQ]);

  useEffect(() => {
    if (activeTab === "promote") loadPromoteRows();
  }, [activeTab, loadPromoteRows]);

  useEffect(() => {
    if (activeTab === "ptm-attendance") {
      (async () => {
        const [ptmRes, studentRes] = await Promise.all([
          api.get("/ptms", {
            params: {
              limit: 500,
              branch_id: branchId || undefined,
              session_id: sessionId || undefined,
            },
          }),
          api.get("/students", {
            params: {
              limit: 2000,
              branch_id: branchId || undefined,
              session_id: sessionId || undefined,
            },
          }),
        ]);
        setPtms(ptmRes.data.items || []);
        setAllStudents(studentRes.data.items || []);
      })();
    }
  }, [activeTab, branchId, sessionId]);

  const selectedClass = classes.find((c) => c.id === classId);
  const availableSections = selectedClass?.sections || [];
  const promoteSelectedClass = classes.find((c) => c.id === promoteClassId);
  const promoteSections = promoteSelectedClass?.sections || [];
  const newSelectedClass = classes.find((c) => c.id === newClassId);
  const newSections = newSelectedClass?.sections || [];
  const idCardSelectedClass = classes.find((c) => c.id === idCardClassId);
  const idCardSections = idCardSelectedClass?.sections || [];

  const buildClassIdCardPath = (extra) => {
    const params = new URLSearchParams();
    if (idCardSection) params.set("section", idCardSection);
    if (branchId) params.set("branch_id", branchId);
    if (sessionId) params.set("session_id", sessionId);
    Object.entries(extra || {}).forEach(([k, v]) => params.set(k, v));
    const query = params.toString();
    return `/students/id-card/class/${idCardClassId}/pdf${query ? `?${query}` : ""}`;
  };

  const previewClassIdCards = async () => {
    if (!idCardClassId) {
      toast.info("Select a class first");
      return;
    }
    setIdCardGenerating(true);
    try {
      await previewAuthedReport(buildClassIdCardPath({ format: "html" }));
    } catch (e) {
      toast.error(
        await extractErrorMessage(e, "Failed to preview class ID cards"),
      );
    } finally {
      setIdCardGenerating(false);
    }
  };

  const downloadClassIdCards = async () => {
    if (!idCardClassId) {
      toast.info("Select a class first");
      return;
    }
    setIdCardGenerating(true);
    try {
      const className = idCardSelectedClass?.name || idCardClassId;
      await downloadAuthedReport(
        buildClassIdCardPath(),
        `id-cards-${className}.pdf`,
      );
    } catch (e) {
      toast.error(
        await extractErrorMessage(e, "Failed to generate class ID cards"),
      );
    } finally {
      setIdCardGenerating(false);
    }
  };

  const exportRows = () => {
    if (rows.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    downloadCSV(
      rows,
      [
        { key: "admission_no", label: "Admission No" },
        { key: "first_name", label: "First Name" },
        { key: "last_name", label: "Last Name" },
        { key: "gender", label: "Gender" },
        { key: "date_of_birth", label: "DOB" },
        { key: "mobile", label: "Mobile" },
        { key: "email", label: "Email" },
        {
          key: "class_id",
          label: "Class",
          csv: (r) => classes.find((c) => c.id === r.class_id)?.name,
        },
        { key: "section", label: "Section" },
        { key: "father_first_name", label: "Father" },
        { key: "mother_first_name", label: "Mother" },
        { key: "aadhaar", label: "Aadhaar" },
        { key: "apaar_id", label: "APAAR ID" },
        { key: "pen", label: "PEN" },
      ],
      "students.csv",
    );
    toast.success("Exported");
  };

  const printStudentProfile = (student) => {
    if (!student) {
      toast.info("No student selected to print");
      return;
    }
    const className =
      classes.find((c) => c.id === student.class_id)?.name || "—";
    const html = buildStudentPrintHtml(student, className, schoolInfo);
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) {
      toast.error("Popup blocked. Allow popups to print.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const downloadStudentIdCard = async (student) => {
    if (!student?.id) {
      toast.info("No student selected");
      return;
    }
    try {
      await downloadAuthedReport(
        `/students/id-card/${student.id}/pdf`,
        `id-card-${student.admission_no || student.id}.pdf`,
      );
    } catch (e) {
      toast.error(await extractErrorMessage(e, "Failed to generate ID card"));
    }
  };

  const reactivateStudent = async (student) => {
    if (!student?.id) return;
    if (!window.confirm("Reactivate this student?")) return;
    await api.post(`/students/${student.id}/reactivate`);
    toast.success("Student reactivated");
    loadDisabled();
    load();
  };

  const downloadSample = () => {
    const sample = [
      {
        first_name: "Aarav",
        last_name: "Sharma",
        gender: "Male",
        date_of_birth: "2012-04-15",
        mobile: "9000123456",
        email: "aarav@example.com",
        class_id: "",
        section_id: "",
        father_first_name: "Rajesh",
        father_mobile: "9000111222",
        mother_first_name: "Kavita",
        address: "12 MG Road",
        city: "Pune",
        state: "Maharashtra",
        pin: "411001",
        aadhaar: "XXXX-XXXX-1234",
        apaar_id: "1234-5678-9012",
        pen: "PEN9876543",
      },
    ];
    downloadCSV(
      sample,
      SAMPLE_CSV_HEADERS.map((k) => ({ key: k, label: k })),
      "students_sample.csv",
    );
    toast.success("Sample template downloaded");
  };

  const onFile = async (e) => {
    if (!directoryPermissions.canAdd) {
      toast.error("You do not have import permission for student directory");
      return;
    }
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const records = parseCSV(text).filter(
      (rec) =>
        rec &&
        (String(rec.first_name || "").trim() ||
          String(rec.last_name || "").trim() ||
          String(rec.mobile || "").trim() ||
          String(rec.email || "").trim()),
    );
    if (records.length === 0) {
      toast.error("No records found in CSV");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    let ok = 0;
    let fail = 0;
    setImportState({
      isImporting: true,
      total: records.length,
      processed: 0,
      ok: 0,
      fail: 0,
    });
    try {
      for (let i = 0; i < records.length; i += 1) {
        const rec = records[i];
        try {
          const payload = {
            ...rec,
            branch_id: branchId,
            session_id: sessionId,
            status: "active",
          };
          if (!payload.class_id) delete payload.class_id;
          if (!payload.section_id) delete payload.section_id;
          await api.post("/students", payload);
          ok += 1;
        } catch {
          fail += 1;
        }
        setImportState((prev) => ({
          ...prev,
          processed: i + 1,
          ok,
          fail,
        }));
      }
      await load();
      toast.success(
        `Uploaded ${ok} of ${records.length} student(s)${fail ? ` · ${fail} failed` : ""}`,
      );
    } finally {
      setImportState((prev) => ({ ...prev, isImporting: false }));
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleStudent = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAllPromote = () => {
    if (selectedIds.length === promoteRows.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(promoteRows.map((r) => r.id));
  };

  const runPromote = async () => {
    if (!promotePermissions.canAdd && !promotePermissions.canEdit) {
      toast.error("You do not have promote permission");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select students to promote");
      return;
    }
    if (!newSessionId || !newClassId) {
      toast.error("New session and new class are required");
      return;
    }

    await api.post("/students/promote", {
      student_ids: selectedIds,
      new_session_id: newSessionId,
      new_class_id: newClassId,
      new_section_id: newSectionId || undefined,
      carry_forward_dues: carryForwardDues,
    });

    toast.success(`Promoted ${selectedIds.length} student(s)`);
    setSelectedIds([]);
    load();
    loadPromoteRows();
  };

  const classNameById = (id) => classes.find((c) => c.id === id)?.name || "—";
  const ptmNameById = (id) => ptms.find((p) => p.id === id)?.name || "—";
  const studentById = (id) => allStudents.find((s) => s.id === id);

  const printSingleAttendance = (items) => {
    if (!items || items.length === 0) {
      toast.info("No records to print");
      return;
    }

    const rowsHtml = items
      .map((r, i) => {
        const stu = studentById(r.student_id);
        const studentName =
          r.name ||
          (stu ? `${stu.first_name || ""} ${stu.last_name || ""}`.trim() : "—");
        const clsName = classNameById(r.class_id || stu?.class_id);
        return `<tr>
          <td>${i + 1}</td>
          <td>${ptmNameById(r.ptm_id)}</td>
          <td>${studentName}</td>
          <td>${clsName}</td>
          <td>${r.section || stu?.section || "—"}</td>
          <td>${(r.date || "").slice(0, 10)}</td>
          <td>${r.time || "—"}</td>
          <td>${r.attended || "—"}</td>
          <td>${r.discussion || "—"}</td>
          <td>${r.suggestion || "—"}</td>
        </tr>`;
      })
      .join("");

    const html = `<!doctype html><html><head><title>PTM Attendance Records</title><style>
      body{font-family:Arial,sans-serif;padding:18px;color:#0f172a}
      h2{margin:0 0 12px 0}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #cbd5e1;padding:6px;vertical-align:top}
      th{background:#f8fafc;text-align:left}
    </style></head><body>
      <h2>PTM Attendance Records</h2>
      <table>
        <thead><tr><th>#</th><th>PTM Name</th><th>Student Name</th><th>Class</th><th>Section</th><th>Date</th><th>Time</th><th>Attended</th><th>Parent Discussion</th><th>Suggestion</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </body></html>`;

    const w = window.open("", "_blank", "width=1280,height=900");
    if (!w) {
      toast.error("Popup blocked. Allow popups to print.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div data-testid="students-page">
      <PageTitle
        title="Students Module"
        subtitle="Admission, promotion, PTM and attendance workflows"
        actions={
          activeTab === "directory" ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={downloadSample}
                className="h-8 hidden sm:inline-flex"
                data-testid="dl-sample"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Sample CSV
              </Button>
              {directoryPermissions.canAdd && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    className="h-8"
                    data-testid="import-btn"
                    disabled={importState.isImporting}
                  >
                    {importState.isImporting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-3.5 h-3.5 mr-1" /> Import
                      </>
                    )}
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={onFile}
                    data-testid="import-file"
                    disabled={importState.isImporting}
                  />
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={exportRows}
                className="h-8"
                data-testid="export-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Excel
              </Button>
              {directoryPermissions.canAdd && (
                <Button
                  size="sm"
                  onClick={() => nav("/students/new")}
                  data-testid="add-admission"
                  className="h-8 erp-primary"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> New Admission
                </Button>
              )}
            </>
          ) : null
        }
      />

      {visibleTabs.length === 0 && !modulePermissions.canView ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
          <div className="text-base font-semibold text-slate-700">
            No student workflow assigned
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Ask super admin to enable at least one Students sub-feature for this
            role.
          </p>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-3 h-9">
            {directoryPermissions.canView && (
              <TabsTrigger
                value="directory"
                data-testid="students-tab-directory"
              >
                Directory
              </TabsTrigger>
            )}
            {directoryPermissions.canView && (
              <TabsTrigger value="disabled" data-testid="students-tab-disabled">
                Disabled Students
              </TabsTrigger>
            )}
            {directoryPermissions.canView && (
              <TabsTrigger value="id-cards" data-testid="students-tab-id-cards">
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                ID Cards
              </TabsTrigger>
            )}
            {promotePermissions.canView && (
              <TabsTrigger value="promote" data-testid="students-tab-promote">
                Promote
              </TabsTrigger>
            )}
            {ptmPermissions.canView && (
              <TabsTrigger value="ptms" data-testid="students-tab-ptms">
                PTM
              </TabsTrigger>
            )}
            {ptmAttendancePermissions.canView && (
              <TabsTrigger
                value="ptm-attendance"
                data-testid="students-tab-ptm-attendance"
              >
                PTM Attendance
              </TabsTrigger>
            )}
          </TabsList>

          {directoryPermissions.canView && (
            <TabsContent value="directory" className="mt-0">
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search name / admission no…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="h-8 pl-7 w-48 sm:w-64 text-xs"
                    data-testid="search-students"
                  />
                </div>
                <select
                  className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
                  value={classId}
                  onChange={(e) => {
                    setClassId(e.target.value);
                    setSection("");
                  }}
                  data-testid="filter-class"
                >
                  <option value="">All Classes</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  data-testid="filter-section"
                  disabled={!classId}
                >
                  <option value="">
                    {classId ? "All Sections" : "Select class first"}
                  </option>
                  {availableSections.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {importState.isImporting && (
                <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>
                    Uploading students: {importState.processed}/
                    {importState.total}
                    {` `}(Success: {importState.ok}, Failed: {importState.fail})
                  </span>
                </div>
              )}

              {directoryLoading ? (
                <ModernLoader
                  compact
                  title="Loading Students"
                  subtitle="Applying filters and preparing directory"
                />
              ) : (
                <DataTable
                  testid="students-table"
                  columns={[
                    {
                      key: "sno",
                      label: "S.No.",
                      render: (_r, index) => index + 1,
                    },
                    {
                      key: "admission_no",
                      label: "Adm. No.",
                      render: (r) => (
                        <button
                          className="erp-text-primary hover:underline font-medium"
                          onClick={() => nav(`/students/profile/${r.id}`)}
                        >
                          {r.admission_no || "—"}
                        </button>
                      ),
                    },
                    {
                      key: "name",
                      label: "Name",
                      render: (r) => (
                        <button
                          className="text-left hover:underline font-medium text-slate-800"
                          onClick={() => nav(`/students/profile/${r.id}`)}
                        >
                          {`${r.first_name || ""} ${r.last_name || ""}`.trim() ||
                            "—"}
                        </button>
                      ),
                    },
                    {
                      key: "class",
                      label: "Class",
                      render: (r) =>
                        classes.find((c) => c.id === r.class_id)?.name || "—",
                    },
                    {
                      key: "section",
                      label: "Sec",
                      render: (r) => r.section || "—",
                    },
                    { key: "gender", label: "Gender" },
                    { key: "mobile", label: "Mobile" },
                    {
                      key: "father_name",
                      label: "Father Name",
                      render: (r) => r.father_name || "—",
                    },
                    {
                      key: "father_mobile",
                      label: "Father Mobile",
                      render: (r) => r.father_mobile || "—",
                    },
                    {
                      key: "mother_name",
                      label: "Mother Name",
                      render: (r) => r.mother_name || "—",
                    },
                    {
                      key: "status",
                      label: "Status",
                      render: (r) => (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${r.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                        >
                          {r.status || "—"}
                        </span>
                      ),
                    },
                  ]}
                  rows={rows}
                  onEdit={
                    directoryPermissions.canEdit
                      ? (r) => nav(`/students/${r.id}`)
                      : undefined
                  }
                  onPrint={printStudentProfile}
                  onIdCard={downloadStudentIdCard}
                  onDelete={
                    directoryPermissions.canDelete
                      ? (r) => setDeleteRow(r)
                      : undefined
                  }
                />
              )}

              <AlertDialog
                open={!!deleteRow}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) setDeleteRow(null);
                }}
              >
                <AlertDialogContent className="max-w-md border-slate-200">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-xl text-slate-900">
                      Delete {deleteRow?.first_name || "student"}?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm leading-6 text-slate-600">
                      This will permanently remove the selected student record.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        if (!deleteRow?.id) return;
                        if (!directoryPermissions.canDelete) {
                          toast.error(
                            "You do not have delete permission for this module",
                          );
                          return;
                        }
                        await api.delete(`/students/${deleteRow.id}`);
                        toast.success("Deleted");
                        setDeleteRow(null);
                        load();
                      }}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      Delete Student
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TabsContent>
          )}

          {directoryPermissions.canView && (
            <TabsContent value="disabled" className="mt-0">
              <div className="mb-3 text-xs text-slate-500">
                Disabled students list with reactivation option.
              </div>
              {directoryLoading ? (
                <ModernLoader
                  compact
                  title="Loading Disabled Students"
                  subtitle="Preparing status-wise student records"
                />
              ) : (
                <DataTable
                  testid="disabled-students-table"
                  columns={[
                    {
                      key: "sno",
                      label: "S.No.",
                      render: (_r, index) => index + 1,
                    },
                    {
                      key: "admission_no",
                      label: "Adm. No.",
                      render: (r) => (
                        <button
                          className="erp-text-primary hover:underline font-medium"
                          onClick={() => nav(`/students/profile/${r.id}`)}
                        >
                          {r.admission_no || "—"}
                        </button>
                      ),
                    },
                    {
                      key: "name",
                      label: "Name",
                      render: (r) => (
                        <button
                          className="text-left hover:underline font-medium text-slate-800"
                          onClick={() => nav(`/students/profile/${r.id}`)}
                        >
                          {`${r.first_name || ""} ${r.last_name || ""}`.trim() ||
                            "—"}
                        </button>
                      ),
                    },
                    {
                      key: "class",
                      label: "Class",
                      render: (r) =>
                        classes.find((c) => c.id === r.class_id)?.name || "—",
                    },
                    {
                      key: "section",
                      label: "Sec",
                      render: (r) => r.section || "—",
                    },
                    {
                      key: "disable_reason",
                      label: "Disable Reason",
                      render: (r) => r.disable_reason || "—",
                    },
                    {
                      key: "disabled_date",
                      label: "Disabled Date",
                      render: (r) => r.disabled_date || "—",
                    },
                    {
                      key: "reactivate",
                      label: "Reactivate",
                      render: (r) => (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => reactivateStudent(r)}
                        >
                          Reactivate
                        </Button>
                      ),
                    },
                  ]}
                  rows={disabledRows}
                  emptyLabel="No disabled students"
                />
              )}
            </TabsContent>
          )}

          {directoryPermissions.canView && (
            <TabsContent value="id-cards" className="mt-0">
              <div className="max-w-xl border rounded-md bg-white border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-800 mb-1">
                  Generate Class-wise ID Cards
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  Select a class (and optionally a section) to generate a
                  front &amp; back PDF for every active student, ready to
                  print and laminate. Configure design, logo and principal
                  signature under Settings → ID Card.
                </p>
                <div className="flex flex-wrap gap-2 items-end mb-4">
                  <div>
                    <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                      Class
                    </Label>
                    <select
                      className="h-9 rounded-md border border-slate-300 text-xs px-2 bg-white w-48"
                      value={idCardClassId}
                      onChange={(e) => {
                        setIdCardClassId(e.target.value);
                        setIdCardSection("");
                      }}
                      data-testid="idcard-filter-class"
                    >
                      <option value="">Select class</option>
                      {classes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                      Section
                    </Label>
                    <select
                      className="h-9 rounded-md border border-slate-300 text-xs px-2 bg-white w-40"
                      value={idCardSection}
                      onChange={(e) => setIdCardSection(e.target.value)}
                      disabled={!idCardClassId}
                      data-testid="idcard-filter-section"
                    >
                      <option value="">
                        {idCardClassId ? "All Sections" : "Select class first"}
                      </option>
                      {idCardSections.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={previewClassIdCards}
                    disabled={!idCardClassId || idCardGenerating}
                    variant="outline"
                    className="h-9"
                    data-testid="preview-class-id-cards"
                  >
                    {idCardGenerating ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Search className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Preview
                  </Button>
                  <Button
                    onClick={downloadClassIdCards}
                    disabled={!idCardClassId || idCardGenerating}
                    className="erp-primary h-9"
                    data-testid="generate-class-id-cards"
                  >
                    {idCardGenerating ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Download PDF
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">
                  Tip: preview first to check the layout, then download once
                  you're happy with it. To generate a card for a single
                  student, use the ID Card icon in the Directory tab's
                  Actions column.
                </p>
              </div>
            </TabsContent>
          )}

          {promotePermissions.canView && (
            <TabsContent value="promote" className="mt-0">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <div className="xl:col-span-2 border rounded-md bg-white border-slate-200">
                  <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2 items-end">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Search student"
                        value={promoteQ}
                        onChange={(e) => setPromoteQ(e.target.value)}
                        className="h-8 pl-7 w-44 text-xs"
                      />
                    </div>
                    <select
                      className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
                      value={promoteClassId}
                      onChange={(e) => {
                        setPromoteClassId(e.target.value);
                        setPromoteSection("");
                      }}
                    >
                      <option value="">All Classes</option>
                      {classes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <select
                      className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
                      value={promoteSection}
                      onChange={(e) => setPromoteSection(e.target.value)}
                      disabled={!promoteClassId}
                    >
                      <option value="">
                        {promoteClassId ? "All Sections" : "Select class first"}
                      </option>
                      {promoteSections.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      onClick={loadPromoteRows}
                    >
                      Load
                    </Button>
                  </div>
                  <div className="px-3 py-2 text-xs text-slate-500 flex items-center justify-between">
                    <span>
                      {selectedIds.length} selected of {promoteRows.length}
                    </span>
                    <button
                      onClick={toggleAllPromote}
                      className="erp-text-primary font-medium hover:underline text-xs"
                    >
                      {selectedIds.length === promoteRows.length &&
                      promoteRows.length > 0
                        ? "Clear all"
                        : "Select all"}
                    </button>
                  </div>
                  <div className="max-h-[420px] overflow-auto divide-y divide-slate-100">
                    {promoteRows.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(s.id)}
                          onChange={() => toggleStudent(s.id)}
                        />
                        <span className="font-mono text-xs text-slate-500 w-24">
                          {s.admission_no || "—"}
                        </span>
                        <span className="font-medium">
                          {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                            "—"}
                        </span>
                        <span className="ml-auto text-xs text-slate-500">
                          {classes.find((c) => c.id === s.class_id)?.name ||
                            "—"}{" "}
                          · {s.section || "—"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border rounded-md bg-white border-slate-200 p-3 h-fit">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">
                    Promote Selected Students
                  </h3>
                  <div className="space-y-2.5">
                    <div>
                      <Label className="text-xs font-semibold">
                        New Session
                      </Label>
                      <select
                        className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                        value={newSessionId}
                        onChange={(e) => setNewSessionId(e.target.value)}
                      >
                        <option value="">Select session</option>
                        {sessions.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">New Class</Label>
                      <select
                        className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                        value={newClassId}
                        onChange={(e) => {
                          setNewClassId(e.target.value);
                          setNewSectionId("");
                        }}
                      >
                        <option value="">Select class</option>
                        {classes.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">
                        New Section
                      </Label>
                      <select
                        className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                        value={newSectionId}
                        onChange={(e) => setNewSectionId(e.target.value)}
                        disabled={!newClassId}
                      >
                        <option value="">
                          {newClassId ? "Select section" : "Select class first"}
                        </option>
                        {newSections.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={carryForwardDues}
                        onChange={(e) => setCarryForwardDues(e.target.checked)}
                      />
                      Carry forward pending dues
                    </label>
                    <Button
                      onClick={runPromote}
                      className="w-full erp-primary"
                      disabled={
                        !promotePermissions.canAdd &&
                        !promotePermissions.canEdit
                      }
                    >
                      <Sparkles className="w-4 h-4 mr-1.5" /> Promote{" "}
                      {selectedIds.length || 0} Student(s)
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {ptmPermissions.canView && (
            <TabsContent value="ptms" className="mt-0">
              <MasterCrudPage
                title="PTM Planner"
                endpoint="/ptms"
                testid="ptms"
                moduleKey="students"
                submoduleKey="ptms"
                scoped
                dateField="date"
                fields={[
                  { key: "name", label: "PTM Name", required: true },
                  { key: "date", label: "Date", type: "date", required: true },
                  { key: "time", label: "Time", type: "time" },
                  {
                    key: "class_id",
                    label: "Class",
                    type: "select",
                    options: classes.map((c) => ({
                      value: c.id,
                      label: c.name,
                    })),
                  },
                  {
                    key: "notes",
                    label: "Notes",
                    type: "textarea",
                    full: true,
                  },
                ]}
              />
            </TabsContent>
          )}

          {ptmAttendancePermissions.canView && (
            <TabsContent value="ptm-attendance" className="mt-0">
              <PtmAttendanceBulkPanel
                classes={classes}
                ptms={ptms}
                branchId={branchId}
                sessionId={sessionId}
              />

              <div className="mt-3 border rounded-md bg-white border-slate-200 p-3">
                <h3 className="text-sm font-semibold text-slate-800 mb-1">
                  Single PTM Attendance Entry
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  Date filter aur search ke saath individual record
                  entry/update.
                </p>
                <MasterCrudPage
                  title="PTM Attendance Records"
                  endpoint="/ptm-attendance"
                  testid="ptm-attendance"
                  moduleKey="students"
                  submoduleKey="ptm_attendance"
                  scoped
                  dateField="date"
                  onPrint={printSingleAttendance}
                  columns={[
                    {
                      key: "ptm_name",
                      label: "PTM Name",
                      render: (r) => ptmNameById(r.ptm_id),
                      csv: (r) => ptmNameById(r.ptm_id),
                    },
                    {
                      key: "student_name",
                      label: "Student Name",
                      render: (r) => {
                        const stu = studentById(r.student_id);
                        return (
                          r.name ||
                          (stu
                            ? `${stu.first_name || ""} ${stu.last_name || ""}`.trim()
                            : "—")
                        );
                      },
                      csv: (r) => {
                        const stu = studentById(r.student_id);
                        return (
                          r.name ||
                          (stu
                            ? `${stu.first_name || ""} ${stu.last_name || ""}`.trim()
                            : "—")
                        );
                      },
                    },
                    {
                      key: "class_name",
                      label: "Class",
                      render: (r) => {
                        const stu = studentById(r.student_id);
                        return classNameById(r.class_id || stu?.class_id);
                      },
                      csv: (r) => {
                        const stu = studentById(r.student_id);
                        return classNameById(r.class_id || stu?.class_id);
                      },
                    },
                    {
                      key: "section",
                      label: "Section",
                      render: (r) => {
                        const stu = studentById(r.student_id);
                        return r.section || stu?.section || "—";
                      },
                      csv: (r) => {
                        const stu = studentById(r.student_id);
                        return r.section || stu?.section || "—";
                      },
                    },
                    { key: "date", label: "PTM Date" },
                    { key: "time", label: "PTM Time" },
                    { key: "attended", label: "Attended" },
                    { key: "discussion", label: "Parent Discussion" },
                    { key: "suggestion", label: "Suggestion" },
                  ]}
                  fields={[
                    {
                      key: "ptm_id",
                      label: "PTM",
                      type: "select",
                      options: ptms.map((p) => ({
                        value: p.id,
                        label: `${p.name}${p.date ? ` (${String(p.date).slice(0, 10)})` : ""}`,
                      })),
                    },
                    {
                      key: "student_id",
                      label: "Student",
                      type: "select",
                      options: rows.map((s) => ({
                        value: s.id,
                        label: `${s.admission_no || "—"} · ${`${s.first_name || ""} ${s.last_name || ""}`.trim()}`,
                      })),
                    },
                    { key: "date", label: "Date", type: "date" },
                    { key: "time", label: "Time", type: "time" },
                    {
                      key: "class_id",
                      label: "Class",
                      type: "select",
                      options: classes.map((c) => ({
                        value: c.id,
                        label: c.name,
                      })),
                    },
                    { key: "section", label: "Section" },
                    {
                      key: "attended",
                      label: "Attended",
                      type: "select",
                      options: [
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                      ],
                    },
                    {
                      key: "discussion",
                      label: "Parent Discussion",
                      type: "textarea",
                      full: true,
                    },
                    {
                      key: "suggestion",
                      label: "Suggestion",
                      type: "textarea",
                      full: true,
                    },
                  ]}
                />
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

function PtmAttendanceBulkPanel({ classes, ptms, branchId, sessionId }) {
  const attendancePermissions = useRouteModulePermissions(
    "students",
    "ptm_attendance",
  );
  const [ptmId, setPtmId] = useState("");
  const [classId, setClassId] = useState("");
  const [section, setSection] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [students, setStudents] = useState([]);
  const [attendanceByStudent, setAttendanceByStudent] = useState({});
  const [discussionByStudent, setDiscussionByStudent] = useState({});
  const [suggestionByStudent, setSuggestionByStudent] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedClass = classes.find((c) => c.id === classId);
  const sections = selectedClass?.sections || [];
  const filteredPtms = classId
    ? ptms.filter((p) => !p.class_id || p.class_id === classId)
    : ptms;

  const selectedPtm = ptms.find((p) => p.id === ptmId);

  useEffect(() => {
    if (!ptmId || !classId) return;
    const ptm = ptms.find((p) => p.id === ptmId);
    if (!ptm) return;
    if (ptm.class_id && ptm.class_id !== classId) {
      setPtmId("");
    }
  }, [classId, ptmId, ptms]);

  useEffect(() => {
    const ptm = ptms.find((p) => p.id === ptmId);
    if (!ptm) return;

    if (ptm.class_id) {
      setClassId(ptm.class_id);
      setSection("");
    }
    if (ptm.date) {
      setDate(String(ptm.date).slice(0, 10));
    }
    if (ptm.time) {
      setTime(String(ptm.time).slice(0, 5));
    }
  }, [ptmId, ptms]);

  const loadStudents = useCallback(async () => {
    if (!classId) {
      toast.error("Select class first");
      return;
    }

    setLoading(true);
    try {
      const params = {
        class_id: classId,
        branch_id: branchId || undefined,
        session_id: sessionId || undefined,
        limit: 1000,
      };
      if (section) params.section = section;

      const [studentsRes, attendanceRes] = await Promise.all([
        api.get("/students", { params }),
        api.get("/ptm-attendance", {
          params: {
            class_id: classId,
            branch_id: branchId || undefined,
            session_id: sessionId || undefined,
            limit: 2000,
          },
        }),
      ]);

      const items = studentsRes.data.items || [];
      const existing = attendanceRes.data.items || [];
      const existingIndex = new Map();
      existing.forEach((r) => {
        if (r.ptm_id && r.student_id) {
          existingIndex.set(`${r.ptm_id}::${r.student_id}`, r);
        }
      });

      setStudents(items);
      setAttendanceByStudent((prev) => {
        const next = {};
        items.forEach((s) => {
          const key = `${ptmId}::${s.id}`;
          const existingRow = existingIndex.get(key);
          next[s.id] = existingRow?.attended || prev[s.id] || "yes";
        });
        return next;
      });
      setDiscussionByStudent((prev) => {
        const next = {};
        items.forEach((s) => {
          const key = `${ptmId}::${s.id}`;
          const existingRow = existingIndex.get(key);
          next[s.id] = existingRow?.discussion || prev[s.id] || "";
        });
        return next;
      });
      setSuggestionByStudent((prev) => {
        const next = {};
        items.forEach((s) => {
          const key = `${ptmId}::${s.id}`;
          const existingRow = existingIndex.get(key);
          next[s.id] = existingRow?.suggestion || prev[s.id] || "";
        });
        return next;
      });
    } catch {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  }, [classId, section, branchId, sessionId, ptmId]);

  const setAllAttendance = (value) => {
    setAttendanceByStudent((prev) => {
      const next = { ...prev };
      students.forEach((s) => {
        next[s.id] = value;
      });
      return next;
    });
  };

  const saveBulkAttendance = async () => {
    if (!attendancePermissions.canAdd && !attendancePermissions.canEdit) {
      toast.error("You do not have permission to save PTM attendance");
      return;
    }
    if (!ptmId) {
      toast.error("Select PTM schedule");
      return;
    }
    if (!classId) {
      toast.error("Select class");
      return;
    }
    if (!date) {
      toast.error("Attendance date is required");
      return;
    }
    if (!time) {
      toast.error("Attendance time is required");
      return;
    }
    if (students.length === 0) {
      toast.error("Load students first");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.get("/ptm-attendance", {
        params: {
          class_id: classId,
          branch_id: branchId || undefined,
          session_id: sessionId || undefined,
          limit: 3000,
        },
      });
      const existing = data.items || [];
      const existingIndex = new Map();
      existing.forEach((r) => {
        if (r.ptm_id && r.student_id && r.id) {
          existingIndex.set(`${r.ptm_id}::${r.student_id}`, r.id);
        }
      });

      const jobs = students.map(async (s) => {
        const payload = {
          ptm_id: ptmId,
          student_id: s.id,
          class_id: classId,
          section: section || s.section || undefined,
          date,
          time,
          attended: attendanceByStudent[s.id] || "no",
          discussion: discussionByStudent[s.id] || "",
          suggestion: suggestionByStudent[s.id] || "",
          notes: [
            discussionByStudent[s.id] || "",
            suggestionByStudent[s.id] || "",
          ]
            .filter(Boolean)
            .join(" | "),
          branch_id: branchId || undefined,
          session_id: sessionId || undefined,
          name: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
        };

        const existingId = existingIndex.get(`${ptmId}::${s.id}`);
        if (existingId) {
          return api.put(`/ptm-attendance/${existingId}`, payload);
        }
        return api.post("/ptm-attendance", payload);
      });

      const settled = await Promise.allSettled(jobs);
      const ok = settled.filter((x) => x.status === "fulfilled").length;
      const fail = settled.length - ok;

      if (ok > 0) {
        toast.success(`Saved attendance for ${ok} student(s)`);
      }
      if (fail > 0) {
        toast.error(`${fail} record(s) failed, retry once`);
      }
    } catch {
      toast.error("Bulk attendance save failed");
    } finally {
      setSaving(false);
    }
  };

  const exportBulkReport = () => {
    if (students.length === 0) {
      toast.info("No loaded students to export");
      return;
    }
    const ptmName = selectedPtm?.name || "PTM";
    const rows = students.map((s) => ({
      admission_no: s.admission_no || "",
      student_name: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
      class_name: selectedClass?.name || "",
      section: s.section || section || "",
      ptm_name: ptmName,
      date,
      time,
      attended: attendanceByStudent[s.id] || "no",
      discussion: discussionByStudent[s.id] || "",
      suggestion: suggestionByStudent[s.id] || "",
    }));
    downloadCSV(
      rows,
      [
        { key: "admission_no", label: "Adm No" },
        { key: "student_name", label: "Student" },
        { key: "class_name", label: "Class" },
        { key: "section", label: "Section" },
        { key: "ptm_name", label: "PTM" },
        { key: "date", label: "Date" },
        { key: "time", label: "Time" },
        { key: "attended", label: "Attendance" },
        { key: "discussion", label: "Discussion" },
        { key: "suggestion", label: "Suggestion" },
      ],
      `ptm_attendance_${selectedClass?.name || "class"}.csv`,
    );
    toast.success("Bulk report exported");
  };

  const printBulkReport = () => {
    if (students.length === 0) {
      toast.info("No loaded students to print");
      return;
    }
    const ptmName = selectedPtm?.name || "PTM";
    const className = selectedClass?.name || "";
    const rowsHtml = students
      .map((s, i) => {
        const studentName = `${s.first_name || ""} ${s.last_name || ""}`.trim();
        return `<tr>
          <td>${i + 1}</td>
          <td>${s.admission_no || ""}</td>
          <td>${studentName}</td>
          <td>${s.section || section || ""}</td>
          <td>${attendanceByStudent[s.id] || "no"}</td>
          <td>${discussionByStudent[s.id] || ""}</td>
          <td>${suggestionByStudent[s.id] || ""}</td>
        </tr>`;
      })
      .join("");
    const html = `<!doctype html><html><head><title>PTM Attendance Report</title><style>
      body{font-family:Arial,sans-serif;padding:18px;color:#0f172a}
      h2{margin:0 0 8px 0}
      .meta{font-size:12px;margin-bottom:10px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #cbd5e1;padding:6px;vertical-align:top}
      th{background:#f8fafc;text-align:left}
    </style></head><body>
      <h2>PTM Attendance Report</h2>
      <div class="meta">PTM: ${ptmName} | Class: ${className} | Section: ${section || "All"} | Date: ${date || ""} | Time: ${time || ""}</div>
      <table>
        <thead><tr><th>#</th><th>Adm No</th><th>Student</th><th>Section</th><th>Attendance</th><th>Discussion</th><th>Suggestion</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </body></html>`;
    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) {
      toast.error("Popup blocked. Allow popups to print.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div
      className="border rounded-md bg-white border-slate-200 p-3"
      data-testid="ptm-attendance-bulk-panel"
    >
      <h3 className="text-sm font-semibold text-slate-800">
        Class-wise PTM Attendance
      </h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">
        PTM schedule select karke poori class ka attendance ek sath mark karein.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2 mb-3">
        <div>
          <Label className="text-xs font-semibold mb-1 block">Class</Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSection("");
            }}
          >
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="lg:col-span-2">
          <Label className="text-xs font-semibold mb-1 block">
            PTM Schedule
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={ptmId}
            onChange={(e) => setPtmId(e.target.value)}
            disabled={!classId}
          >
            <option value="">
              {classId ? "Select PTM" : "Select class first"}
            </option>
            {filteredPtms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.date ? `(${String(p.date).slice(0, 10)}` : ""}
                {p.time ? ` ${String(p.time).slice(0, 5)}` : ""}
                {p.date ? ")" : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs font-semibold mb-1 block">Section</Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={section}
            onChange={(e) => setSection(e.target.value)}
            disabled={!classId}
          >
            <option value="">All</option>
            {sections.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs font-semibold mb-1 block">Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9"
          />
        </div>

        <div>
          <Label className="text-xs font-semibold mb-1 block">Time</Label>
          <Input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-9"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          variant="outline"
          className="h-8"
          onClick={loadStudents}
          disabled={loading}
        >
          {loading ? "Loading..." : "Load Class Students"}
        </Button>
        <Button
          variant="outline"
          className="h-8"
          onClick={() => setAllAttendance("yes")}
          disabled={
            students.length === 0 ||
            (!attendancePermissions.canAdd && !attendancePermissions.canEdit)
          }
        >
          Mark All Present
        </Button>
        <Button
          variant="outline"
          className="h-8"
          onClick={() => setAllAttendance("no")}
          disabled={
            students.length === 0 ||
            (!attendancePermissions.canAdd && !attendancePermissions.canEdit)
          }
        >
          Mark All Absent
        </Button>
        <Button
          className="h-8 erp-primary"
          onClick={saveBulkAttendance}
          disabled={
            saving ||
            students.length === 0 ||
            (!attendancePermissions.canAdd && !attendancePermissions.canEdit)
          }
        >
          {saving ? "Saving..." : `Save ${students.length} Attendance`}
        </Button>
        <Button
          variant="outline"
          className="h-8"
          onClick={exportBulkReport}
          disabled={students.length === 0}
        >
          Excel Export
        </Button>
        <Button
          variant="outline"
          className="h-8"
          onClick={printBulkReport}
          disabled={students.length === 0}
        >
          Print Report
        </Button>
      </div>

      <div className="border rounded-md border-slate-200 overflow-hidden">
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full min-w-[1100px] border-collapse">
            <thead className="bg-slate-50 text-slate-700 text-xs uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left">Adm No</th>
                <th className="px-3 py-2 text-left">Student</th>
                <th className="px-3 py-2 text-left">Section</th>
                <th className="px-3 py-2 text-left">Attendance</th>
                <th className="px-3 py-2 text-left">Parent Discussion</th>
                <th className="px-3 py-2 text-left">Suggestion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-xs text-slate-400">
                    Class load karke yahan attendance + remarks mark karein.
                  </td>
                </tr>
              ) : (
                students.map((s) => (
                  <tr key={s.id} className="text-sm align-top">
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">
                      {s.admission_no || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                        "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {s.section || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white w-32"
                        value={attendanceByStudent[s.id] || "yes"}
                        onChange={(e) =>
                          setAttendanceByStudent((prev) => ({
                            ...prev,
                            [s.id]: e.target.value,
                          }))
                        }
                      >
                        <option value="yes">Present</option>
                        <option value="no">Absent</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={discussionByStudent[s.id] || ""}
                        onChange={(e) =>
                          setDiscussionByStudent((prev) => ({
                            ...prev,
                            [s.id]: e.target.value,
                          }))
                        }
                        placeholder="Parent discussion"
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={suggestionByStudent[s.id] || ""}
                        onChange={(e) =>
                          setSuggestionByStudent((prev) => ({
                            ...prev,
                            [s.id]: e.target.value,
                          }))
                        }
                        placeholder="Suggestion"
                        className="h-8 text-xs"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
